import { useEffect } from 'react'
import { useRouteError, isRouteErrorResponse } from 'react-router-dom'
import { telemetry } from '@/telemetry'

// Rendered by React Router as the root route's `errorElement`. Catches
// render errors thrown by any descendant route element and router
// loader/action errors, so the user sees a graceful fallback with a way
// back instead of a white screen (per-page contract: no white-screen).
export function RouteErrorBoundary() {
  const error = useRouteError()

  let detail: string
  if (isRouteErrorResponse(error)) {
    detail = `${error.status} ${error.statusText}`
  } else if (error instanceof Error) {
    detail = error.message
  } else {
    detail = 'An unexpected error occurred.'
  }

  // Report the crash to App Insights (no-op when telemetry isn't configured;
  // trackException can never throw, so no guarding needed here). Non-Error
  // values (router ErrorResponses, thrown plain objects) would stringify to
  // "[object Object]" in App Insights — wrap them in an Error carrying the
  // human-readable detail instead. `detail` is a pure derivation of `error`,
  // so including it in deps can never re-fire on its own.
  useEffect(() => {
    telemetry.trackException(
      error instanceof Error ? error : new Error(detail),
      { source: 'RouteErrorBoundary' },
    )
  }, [error, detail])

  // Recovery uses real document navigation (a plain <a> / location call), NOT
  // SPA routing: the most common error this boundary catches is a failed
  // dynamic import ("Failed to fetch dynamically imported module" — a stale
  // chunk after a deploy, or dev HMR churn). SPA navigation would just
  // re-attempt the broken import and stay stuck; a full reload re-fetches the
  // app fresh and actually recovers.
  return (
    <div className="mx-auto max-w-xl py-16 px-4 text-center">
      <h1 className="text-2xl font-heading font-bold text-navy">
        Something went wrong
      </h1>
      <p className="mt-4 text-slate-600">
        This page hit an unexpected error. Your data is safe — try again, or
        head back home.
      </p>

      {/* Raw error detail is for developers, never users (punch-list #9: never
          leak raw error/API/deploy detail into the UI). Shown in dev only. */}
      {import.meta.env.DEV && (
        <pre className="mt-3 overflow-x-auto rounded bg-slate-100 px-3 py-2 text-left text-xs text-slate-500">
          {detail}
        </pre>
      )}

      <div className="mt-8 flex items-center justify-center gap-4">
        <button
          type="button"
          onClick={() => window.location.reload()}
          className="rounded bg-navy px-4 py-2 text-sm font-semibold text-white hover:bg-navy/90"
        >
          Try again
        </button>
        <a href="/" className="text-blue hover:underline">
          Back to home
        </a>
      </div>
    </div>
  )
}
